<!--
  Модалка импорта расходов из файла: загрузка файла, выбор параметров
  импорта и запуск обработки на backend
-->
<template>
  <BaseModal
    :visible="true"
    title="Импорт расходов из 1С"
    size="md"
    @close="handleClose"
  >
    <div class="import-modal-content">
      <!-- Оверлей ожидания импорта -->
      <div v-if="isWaiting" class="import-overlay">
        <div class="spinner"></div>
        <p class="import-overlay-text">Импорт выполняется...</p>
      </div>

      <!-- Индикатор шагов -->
      <div class="steps-indicator">
        <div
          class="step"
          :class="{ active: step === 1, done: step > 1 }"
        >
          <div class="step-circle">
            <svg
              v-if="step > 1"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span v-else>1</span>
          </div>
          <span class="step-label">Загрузка файла</span>
        </div>
        <div class="step-line" :class="{ done: step > 1 }"></div>
        <div class="step" :class="{ active: step === 2 }">
          <div class="step-circle">
            <span>2</span>
          </div>
          <span class="step-label">Параметры импорта</span>
        </div>
      </div>

      <!-- Шаг 1: загрузка файла -->
      <div v-if="step === 1" class="step-body">
        <div
          class="dropzone"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @drop.prevent="handleDrop"
          :class="{
            'drag-over': dragOver,
            'has-file': uploadedFile,
          }"
        >
          <div
            v-if="!uploadedFile && !isUploading"
            class="dropzone-content"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="dropzone-icon"
            >
              <path
                d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
              ></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <p class="dropzone-text">Перетащите файл сюда или</p>
            <label :for="fileInputId" class="browse-btn">
              Выберите файл
            </label>
            <input
              :id="fileInputId"
              type="file"
              accept=".txt,.csv,.xml"
              @change="handleFileSelect"
              class="file-input"
            />
            <p class="dropzone-hint">
              Форматы: TXT, CSV, XML · Макс. 30MB
            </p>
          </div>

          <div v-else-if="isUploading" class="dropzone-content">
            <base-loader />
            <p class="dropzone-text">Загрузка файла...</p>
            <div class="upload-progress">
              <div class="progress-track">
                <div
                  class="progress-fill"
                  :style="{ width: uploadProgress + '%' }"
                ></div>
              </div>
              <span class="progress-label">
                {{ uploadProgress }}%
              </span>
            </div>
          </div>

          <div v-else class="dropzone-content dropzone-success">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="success-icon"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <p class="dropzone-text success-text">
              {{ uploadedFile.name }}
            </p>
            <p class="dropzone-hint">
              {{ formatFileSize(uploadedFile.size) }} · Загружен
              успешно
            </p>
            <button class="change-file-btn" @click="resetFile">
              Заменить файл
            </button>
          </div>
        </div>

        <div v-if="uploadError" class="error-message">
          {{ uploadError }}
        </div>

        <div class="step-actions">
          <base-primary-button
            @click="goToStep2"
            :disabled="!attachmentId"
          >
            Далее
          </base-primary-button>
          <base-button @click="handleClose">Отмена</base-button>
        </div>
      </div>

      <!-- Шаг 2: параметры импорта -->
      <div v-if="step === 2" class="step-body">
        <div class="uploaded-file-info">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>{{ uploadedFile.name }}</span>
        </div>

        <!-- Источник оплаты -->
        <div class="form-group">
          <base-label :required="true">Источник оплаты</base-label>
          <PaymentSourceSelect
            v-model="formData.payment_source_id"
            :invalid="errors.payment_source_id"
            :with-button="true"
            button-label="Создать источник оплаты"
            @update:modelValue="errors.payment_source_id = false"
            @button-click="toggleInlinePaymentSourceForm"
          />
          <span v-if="errors.payment_source_id" class="error-message">
            Поле обязательно для заполнения
          </span>
        </div>

        <!-- Инлайн-форма создания источника оплаты -->
        <transition name="fade-slide">
          <div
            v-if="showInlinePaymentSourceForm"
            class="inline-nested-form"
          >
            <div class="inline-form-header">
              <span class="inline-form-title">
                Новый источник оплаты
              </span>
              <button
                class="inline-form-close"
                type="button"
                @click="showInlinePaymentSourceForm = false"
              >
                ✕
              </button>
            </div>
            <PaymentSourceForm
              :is-loading="isPaymentSourceCreating"
              submit-label="Создать и выбрать"
              @save="saveInlinePaymentSource"
              @cancel="showInlinePaymentSourceForm = false"
            />
          </div>
        </transition>

        <!-- Категория расходов -->
        <div class="form-group">
          <base-label :required="true">Категория расходов</base-label>
          <ExpenseCategorySelect
            v-model="formData.expense_category_id"
            :clearable="false"
            :invalid="errors.expense_category_id"
            :with-button="true"
            :internal-create="false"
            button-label="Создать категорию"
            @update:modelValue="errors.expense_category_id = false"
            @button-click="toggleInlineCategoryForm"
          />
          <span
            v-if="errors.expense_category_id"
            class="error-message"
          >
            Поле обязательно для заполнения
          </span>
        </div>

        <!-- Инлайн-форма создания категории расходов -->
        <transition name="fade-slide">
          <div
            v-if="showInlineCategoryForm"
            class="inline-nested-form"
          >
            <div class="inline-form-header">
              <span class="inline-form-title">
                Новая категория расходов
              </span>
              <button
                class="inline-form-close"
                type="button"
                @click="showInlineCategoryForm = false"
              >
                ✕
              </button>
            </div>
            <ExpenseCategoryForm
              :is-loading="isCategoryCreating"
              submit-label="Создать и выбрать"
              @save="saveInlineCategory"
              @cancel="showInlineCategoryForm = false"
            />
          </div>
        </transition>

        <div class="step-actions">
          <base-primary-button
            @click="handleSubmit"
            :loading="isSubmitting"
          >
            Импортировать
          </base-primary-button>
          <base-button @click="step = 1" :disabled="isSubmitting">
            Назад
          </base-button>
          <base-button @click="handleClose" :disabled="isSubmitting">
            Отмена
          </base-button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { getCompanyId } from "@/utils/userUtils";
import { formatFileSize, isFileTooLarge } from "@/utils/fileUtils";

import { useNotificationStore } from "@/stores/notificationStore";
import { usePaymentSourceStore } from "@/stores/paymentSourceStore";
import { useExpenseCategoryStore } from "@/stores/expenseCategoryStore";

import attachmentsApi from "@/services/api/attachmentsApi";
import expensesApi from "@/services/api/expensesApi";

import PaymentSourceSelect from "@/components/Expense/PaymentSourceSelect.vue";
import PaymentSourceForm from "@/components/Expense/PaymentSourceForm.vue";
import ExpenseCategorySelect from "@/components/Expense/ExpenseCategorySelect.vue";
import ExpenseCategoryForm from "@/components/Expense/ExpenseCategoryForm.vue";

export default {
  name: "ImportExpensesModal",
  components: {
    PaymentSourceSelect,
    PaymentSourceForm,
    ExpenseCategorySelect,
    ExpenseCategoryForm,
  },
  emits: ["close", "import-success"],
  data() {
    return {
      step: 1,
      companyId: getCompanyId(),

      dragOver: false,
      isUploading: false,
      uploadProgress: 0,
      uploadError: null,
      uploadedFile: null,
      attachmentId: null,

      isWaiting: false,

      showInlinePaymentSourceForm: false,
      isPaymentSourceCreating: false,

      showInlineCategoryForm: false,
      isCategoryCreating: false,

      formData: {
        payment_source_id: null,
        expense_category_id: null,
      },

      errors: {
        payment_source_id: false,
        expense_category_id: false,
      },

      isSubmitting: false,
    };
  },
  computed: {
    fileInputId() {
      return "import-1c-file-upload";
    },
    notificationStore() {
      return useNotificationStore();
    },
  },
  methods: {
    formatFileSize,

    handleDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) this.uploadFile(file);
    },

    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.uploadFile(file);
      e.target.value = "";
    },

    async uploadFile(file) {
      if (isFileTooLarge(file)) {
        this.uploadError = "Файл слишком большой (макс. 30MB)";
        return;
      }

      this.uploadError = null;
      this.isUploading = true;
      this.uploadProgress = 0;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("attachable_type", "Requisite");

      try {
        const response = await attachmentsApi.create(
          this.companyId,
          formData,
          true,
          {
            onUploadProgress: (progressEvent) => {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        this.attachmentId = response.data.result.id;
        this.uploadedFile = {
          name: file.name,
          size: file.size,
        };
      } catch (error) {
        this.uploadError =
          "Ошибка при загрузке файла. Попробуйте снова.";
        console.error("Ошибка загрузки аттачмента:", error);
      } finally {
        this.isUploading = false;
      }
    },

    resetFile() {
      this.uploadedFile = null;
      this.attachmentId = null;
      this.uploadError = null;
      this.uploadProgress = 0;
    },

    goToStep2() {
      if (!this.attachmentId) return;
      this.step = 2;
    },

    toggleInlinePaymentSourceForm() {
      this.showInlinePaymentSourceForm =
        !this.showInlinePaymentSourceForm;
      if (this.showInlinePaymentSourceForm) {
        this.showInlineCategoryForm = false;
      }
    },

    toggleInlineCategoryForm() {
      this.showInlineCategoryForm = !this.showInlineCategoryForm;
      if (this.showInlineCategoryForm) {
        this.showInlinePaymentSourceForm = false;
      }
    },

    async saveInlinePaymentSource(formData) {
      this.isPaymentSourceCreating = true;
      try {
        const store = usePaymentSourceStore();
        const created = await store.createSource(formData);

        this.formData.payment_source_id = created.id;
        this.errors.payment_source_id = false;
        this.showInlinePaymentSourceForm = false;

        this.notificationStore.showSuccess(
          "Источник оплаты создан и выбран"
        );
      } catch (error) {
        console.error("Ошибка при создании источника оплаты:", error);
      } finally {
        this.isPaymentSourceCreating = false;
      }
    },

    async saveInlineCategory(formData) {
      this.isCategoryCreating = true;
      try {
        const store = useExpenseCategoryStore();
        const created = await store.createExpenseCategory(formData);

        this.formData.expense_category_id = created.id;
        this.errors.expense_category_id = false;
        this.showInlineCategoryForm = false;

        this.notificationStore.showSuccess(
          "Категория расходов создана и выбрана"
        );
      } catch (error) {
        console.error(
          "Ошибка при создании категории расходов:",
          error
        );
      } finally {
        this.isCategoryCreating = false;
      }
    },

    validate() {
      let isValid = true;
      this.errors = {
        payment_source_id: false,
        expense_category_id: false,
      };

      if (!this.formData.payment_source_id) {
        this.errors.payment_source_id = true;
        isValid = false;
      }
      if (!this.formData.expense_category_id) {
        this.errors.expense_category_id = true;
        isValid = false;
      }

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля"
        );
      }

      return isValid;
    },

    async handleSubmit() {
      if (!this.validate()) return;

      this.isSubmitting = true;
      try {
        await expensesApi.import(this.companyId, {
          attachment_id: this.attachmentId,
          payment_source_id: this.formData.payment_source_id,
          expense_category_id: this.formData.expense_category_id,
          source_provider: "bank_statement",
        });

        this.isWaiting = true;

        this.$emit("import-success");
      } catch (error) {
        console.error("Ошибка импорта расходов:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    handleClose() {
      if (this.isUploading || this.isSubmitting || this.isWaiting)
        return;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.import-modal-content {
  position: relative;
  min-height: 300px;
}

.import-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
  gap: 16px;
}

.import-overlay-text {
  font-size: 14px;
  color: var(--color-dark);
  font-weight: 500;
}

.steps-indicator {
  display: flex;
  align-items: center;
  padding: 0 4px;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--color-grey);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #999;
  background: #fff;
  transition: all 0.3s ease;
}

.step.active .step-circle {
  border-color: var(--color-green);
  color: var(--color-green);
}

.step.done .step-circle {
  border-color: var(--color-green);
  background: var(--color-green);
  color: #fff;
}

.step-label {
  font-size: 13px;
  color: #999;
  transition: color 0.3s ease;
}

.step.active .step-label,
.step.done .step-label {
  color: var(--color-dark);
  font-weight: 500;
}

.step-line {
  flex: 1;
  height: 2px;
  background: var(--color-grey);
  margin: 0 12px;
  transition: background 0.3s ease;
}

.step-line.done {
  background: var(--color-green);
}

.step-body {
  padding: 20px 4px 4px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.dropzone {
  border: 2px dashed var(--color-grey);
  border-radius: 10px;
  padding: 32px 20px;
  margin-bottom: var(--base-in-indent);
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.dropzone.drag-over {
  border-color: var(--color-green);
  background-color: rgba(0, 191, 165, 0.04);
}

.dropzone.has-file {
  border-color: var(--color-green);
  background-color: rgba(0, 191, 165, 0.04);
  cursor: default;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.dropzone-icon {
  color: #bbb;
}

.dropzone-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.dropzone-hint {
  font-size: 12px;
  color: #aaa;
  margin: 0;
}

.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
}

.browse-btn {
  background-color: var(--color-green);
  color: #fff;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.browse-btn:hover {
  background-color: var(--color-green-hover);
}

.upload-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 240px;
}

.progress-track {
  flex: 1;
  height: 6px;
  background: var(--color-grey);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-green);
  border-radius: 3px;
  transition: width 0.2s ease;
}

.progress-label {
  font-size: 12px;
  color: #666;
  min-width: 32px;
}

.dropzone-success {
  gap: 8px;
}

.success-icon {
  color: var(--color-green);
}

.success-text {
  font-weight: 500;
  color: var(--color-dark);
}

.change-file-btn {
  margin-top: 4px;
  background: none;
  border: none;
  color: var(--color-green);
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.change-file-btn:hover {
  color: var(--color-green-hover);
}

.uploaded-file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: var(--base-in-indent);
  background: rgba(0, 191, 165, 0.06);
  border: 1px solid rgba(0, 191, 165, 0.25);
  border-radius: 8px;
  font-size: 13px;
  color: var(--color-dark);
}

.uploaded-file-info svg {
  color: var(--color-green);
  flex-shrink: 0;
}

.import-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: var(--base-in-indent);
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}

.import-info-icon {
  color: var(--color-yellow);
  flex-shrink: 0;
  margin-top: 1px;
}

.import-info-text {
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
}

.inline-nested-form {
  border-radius: 8px;
  padding: 12px;
  margin-bottom: var(--base-in-indent);
  border: 2px dashed #b6b5b5;
  background: #eff6ff;
}

.inline-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.inline-form-title {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
}

.inline-form-close {
  background: none;
  border: none;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.inline-form-close:hover {
  color: #374151;
}

.step-actions {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}

.error-message {
  display: block;
  margin: 5px 0 20px;
  color: var(--color-danger);
  font-size: 12px;
}
</style>
